import { useState, useRef, useEffect } from 'react';

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  label?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  required = false,
  label,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sort options alphabetically
  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));

  // Filter options based on search query
  const filteredOptions = sortedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected option label
  const selectedOption = options.find((opt) => opt.value === value);
  const selectedLabel = selectedOption?.label || placeholder;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs text-slate-400 uppercase font-medium">
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative">
        {/* Selected value display / trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all text-left flex items-center justify-between ${className}`}
        >
          <span className={value ? 'text-white' : 'text-slate-500'}>
            {selectedLabel}
          </span>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl shadow-2xl max-h-80 flex flex-col">
            {/* Search input */}
            <div className="p-3 border-b border-[var(--border-primary)]">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full bg-[var(--bg-2)] border border-[var(--border-primary)] rounded-lg px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-sm"
                autoFocus
              />
            </div>

            {/* Options list */}
            <div className="overflow-y-auto flex-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`w-full px-4 py-3 text-left hover:bg-purple-500/10 transition-colors ${
                      option.value === value
                        ? 'bg-purple-500/20 text-purple-300'
                        : 'text-white'
                    }`}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hidden input for form validation */}
        {required && (
          <input
            type="text"
            value={value}
            onChange={() => {}}
            required
            className="absolute inset-0 opacity-0 pointer-events-none"
            tabIndex={-1}
          />
        )}
      </div>
    </div>
  );
}
